import React, { useCallback, useMemo, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useLocation, useHistory } from 'react-router';

import { useSignOutMutation, useStopImpersonationMutation } from 'api/main';
import { appNotificationSelectors, userSelectors } from 'reducers';
import { logoutOpen } from 'reducers/user';
import { useGetReferralParameters, useSmallScreen, useUserRoleMatchCategory } from 'utils/hooks';
import { RoleNames, roleCategories } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, InfoButton, CustomPopper, ConfirmDialog } from 'components';
import { Box, DialogContentText, IconButton, MenuItem, MenuList } from '@mui/material';
import * as icons from 'mdi-material-ui';

const quitButtonSx = theme => ({
    color: theme.palette.grey[600],
    padding: 0,
    flexDirection: 'column',
    alignSelf: 'flex-end',

    '&:hover': {
        background: 'transparent',
        color: theme.palette.primary.main,
    },
});

const iconSx = theme => ({
    fontSize: '40px',
    color: theme.palette.grey[600],

    '&:hover': {
        color: theme.palette.primary.main,
    },
});

const menuItemSx = {
    textTransform: 'uppercase',
    justifyContent: 'center',
    height: '56px',
    width: '180px',
    fontSize: '13px',
    textAlign: 'center',
    whiteSpace: 'break-spaces',
};

const QuitButton = ({ inTunnel, ...props }) => {
    const dispatch = useDispatch();
    const anchorRef = useRef(null);
    const location = useLocation();
    const history = useHistory();
    const isSmall = useSmallScreen('md');

    const [stopImpersonation, { isLoading: impersonateLoader }] = useStopImpersonationMutation();
    const [signOut] = useSignOutMutation();

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuOpenInfoDialog, setMenuOpenInfoDialog] = useState(false);
    const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);

    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const userIsAdminDistributor = useUserRoleMatchCategory(roleCategories.ADMIN_DISTRIBUTORS);
    const userIsDistributor = useUserRoleMatchCategory(roleCategories.DISTRIBUTORS);

    const internalUser = useSelector(userSelectors.getInternalUser);
    const loggedIn = useSelector(userSelectors.getLoggedInStatus);
    const isCreatingSubscription = useSelector(appNotificationSelectors.isCreatingSubscription);
    const isNewSubscription = useMemo(
        () => location.pathname.includes('new-subscription'),
        [location]
    );
    const { product } = useGetReferralParameters();

    const quitText = useMemo(
        () =>
            internalUser?.impersonatedByUser ? (
                texts.QUITTER
            ) : (
                <React.Fragment>
                    <Box component="span" display="block">
                        {texts.DECONNEXION} {isSmall && parseFullName(internalUser)}
                    </Box>
                    {!isSmall && (
                        <Box component="span" display="block">
                            {parseFullName(internalUser)}
                            {internalUser?.investorCompany?.corporateName
                                ? ` (${internalUser?.investorCompany?.corporateName})`
                                : ''}
                        </Box>
                    )}
                </React.Fragment>
            ),
        [internalUser, isSmall]
    );
    const isNonInitialSubscription = useMemo(
        () => location.pathname.includes('new-subscription'),
        [location]
    );
    const isAddingIndirectSubscription = useMemo(
        () => location.pathname.includes('add-indirect-subscription'),
        [location]
    );
    const isImpersonatedByDistributor = useMemo(
        () =>
            [RoleNames.ADMIN_CIF, RoleNames.PSI, RoleNames.COMMERCIALISATEUR].includes(
                internalUser?.impersonatorRole
            ),
        [internalUser]
    );

    const handleQuit = useCallback(() => {
        if (isNewSubscription && isImpersonatedByDistributor) {
            setConfirmDisconnectOpen(true);
        } else if (
            (isNewSubscription && !isImpersonatedByDistributor) ||
            isNonInitialSubscription
        ) {
            dispatch(push('/dashboard'));
        } else if (isCreatingSubscription) {
            dispatch(push(product && userIsDistributor ? '/homepage' : '/subscriptions'));
        } else if (isAddingIndirectSubscription) {
            history.goBack();
        } else if (internalUser?.impersonatedByUser) {
            setConfirmDisconnectOpen(true);
        } else if (inTunnel) {
            dispatch(logoutOpen(true));
        } else {
            signOut(true);
        }
        setMenuOpen(false);
    }, [
        isNewSubscription,
        isImpersonatedByDistributor,
        isNonInitialSubscription,
        isCreatingSubscription,
        isAddingIndirectSubscription,
        internalUser,
        inTunnel,
        dispatch,
        product,
        userIsDistributor,
        history,
        signOut,
    ]);

    const handleInfoAction = useCallback(() => {
        setMenuOpenInfoDialog(true);
        setMenuOpen(false);
    }, []);

    return (
        <Box {...props}>
            {loggedIn &&
                (isSmall ? (
                    <IconButton
                        ref={anchorRef}
                        onClick={() => setMenuOpen(prevOpen => !prevOpen)}
                        color={menuOpen ? 'primary' : 'grey'}
                        sx={quitButtonSx}
                        disableRipple
                        size="large"
                    >
                        <icons.Menu sx={iconSx} />
                    </IconButton>
                ) : (
                    <ActionButton
                        size="small"
                        variant="text"
                        sx={quitButtonSx}
                        onClick={handleQuit}
                    >
                        {isNonInitialSubscription && !isImpersonatedByDistributor
                            ? texts.MY_SPACE
                            : isCreatingSubscription
                            ? texts.RETOUR
                            : quitText}
                    </ActionButton>
                ))}
            {loggedIn &&
                !isNonInitialSubscription &&
                !isCreatingSubscription &&
                !isSmall &&
                !internalUser?.impersonatedByUser &&
                inTunnel && (
                    <InfoButton
                        disableMarginBottom
                        title={`${texts.A_NOTTER}:`}
                        sx={{ marginLeft: 0, alignSelf: 'flex-end' }}
                        content={
                            <React.Fragment>
                                <DialogContentText>{texts.VOUS_POUVEZ_REVENIR}</DialogContentText>
                                <DialogContentText>{texts.POUR_CELA}</DialogContentText>
                            </React.Fragment>
                        }
                        menuOpenInfoDialog={menuOpenInfoDialog}
                        setMenuOpenInfoDialog={setMenuOpenInfoDialog}
                    />
                )}
            {isSmall && (
                <CustomPopper
                    open={menuOpen}
                    anchorEl={anchorRef.current}
                    handleClose={() => setMenuOpen(false)}
                >
                    <MenuList disablePadding autoFocusItem={menuOpen}>
                        {!isNonInitialSubscription && (
                            <MenuItem disableRipple sx={menuItemSx} onClick={handleInfoAction}>
                                {texts.A_NOTTER}
                            </MenuItem>
                        )}
                        <MenuItem disableRipple sx={menuItemSx} onClick={handleQuit}>
                            {isNonInitialSubscription
                                ? texts.MY_SPACE
                                : isCreatingSubscription
                                ? texts.RETOUR
                                : quitText}
                        </MenuItem>
                    </MenuList>
                </CustomPopper>
            )}
            <ConfirmDialog
                loading={impersonateLoader}
                title={
                    userIsAdminDistributor || userIsDistributor
                        ? texts.STOP_IMPERSONATE_DISTRIBUTOR
                        : userIsInvestor
                        ? texts.STOP_IMPERSONATE_SUBSCRIBER
                        : texts.ARRETER_SUBSTITUTION
                }
                open={confirmDisconnectOpen}
                handleClose={() => setConfirmDisconnectOpen(false)}
                handleConfirm={stopImpersonation}
            >
                {texts.VOULOIR_DECONNECTER}
            </ConfirmDialog>
        </Box>
    );
};

QuitButton.propTypes = {
    inTunnel: PropTypes.bool,
};

export default QuitButton;
